import type { CategoryNodeType } from "src/molecules/CategoryTree/types";
import type {
  ConstructorListingRequest,
  ListingFacetKeys,
  ListingFacets,
  ListingSortOption,
  ListingSortOptions,
  SelectedFacets,
} from "src/types/adapters/listing.type";
import type { CatalogProduct } from "src/types/graphql.d";
import type { SerializedListingResult } from "src/types/product-listing";
import type { TreeType } from "src/types/Tree";
import { deepEqual } from "fast-equals";

export type ListingStoreState = {
  products: Record<number, CatalogProduct[]>;
  sort: string;
  sortOption?: ListingSortOption;
  sortOptions: ListingSortOptions;
  facets?: ListingFacets;
  tree?: TreeType<CategoryNodeType>;
  appliedFacets: SelectedFacets;
  page: number;
  totalPages: number;
  totalResults: number;
  resultId?: string;
  revision: number;
  loading: boolean;
  hasFetched: boolean;
  request?: ConstructorListingRequest;
};

export function pagesUpTo(
  products: ListingStoreState["products"],
  page: number
): ListingStoreState["products"] {
  return Object.fromEntries(
    Object.entries(products).filter(([loaded]) => Number(loaded) <= page)
  );
}

const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) {
    listener();
  }
}

const INITIAL_STATE: ListingStoreState = {
  facets: undefined,
  tree: undefined,
  appliedFacets: {},
  page: 0,
  products: {},
  revision: 0,
  sort: "",
  sortOption: undefined,
  sortOptions: {},
  totalPages: 0,
  totalResults: 0,
  loading: false,
  hasFetched: false,
};

export function buildListingStateFromInitial(
  initial: SerializedListingResult | undefined
): ListingStoreState {
  if (!initial) {
    return INITIAL_STATE;
  }

  const page = initial.page ?? 0;

  return {
    ...INITIAL_STATE,
    page,
    products: { [page]: initial.products },
    facets: new Map(initial.facets),
    sortOptions: initial.sortOptions,
    totalPages: initial.totalPages,
    totalResults: initial.totalResults,
    resultId: initial.resultId,
    request: initial.request,
  };
}

let listingStoreState: ListingStoreState = INITIAL_STATE;

function resetForNewQuery(patch: Partial<ListingStoreState>) {
  listingStoreState = {
    ...listingStoreState,
    ...patch,
    page: 0,
    products: {},
    loading: false,
    revision: listingStoreState.revision + 1,
  };

  emit();
}

export const listingStore = {
  subscribe: (listener: () => void) => {
    listeners.add(listener);

    return () => listeners.delete(listener);
  },

  emit,

  getSnapshot: () => {
    return listingStoreState;
  },

  setState: (state: Partial<ListingStoreState>) => {
    if (
      state.revision !== undefined &&
      state.revision !== listingStoreState.revision
    ) {
      return;
    }

    listingStoreState = {
      ...listingStoreState,
      ...state,
    };

    emit();
  },

  setFacet: (facets: ListingStoreState["facets"]) => {
    resetForNewQuery({ facets });

    emit();
  },

  toggleFacetValue: (key: ListingFacetKeys, value: string) => {
    const current = listingStoreState.appliedFacets[key] || [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];

    resetForNewQuery({
      appliedFacets: {
        ...listingStoreState.appliedFacets,
        [key]: next,
      },
    });
  },

  setFacetValues: (key: ListingFacetKeys, values: string[]) => {
    if (values.length === 0) {
      const { [key]: _removed, ...rest } = listingStoreState.appliedFacets;
      resetForNewQuery({ appliedFacets: rest });
      return;
    }

    resetForNewQuery({
      appliedFacets: {
        ...listingStoreState.appliedFacets,
        [key]: values,
      },
    });
  },

  setAppliedFacets: (appliedFacets: SelectedFacets) => {
    resetForNewQuery({ appliedFacets });
  },

  clearFacet: (key: ListingFacetKeys) => {
    const { [key]: _removed, ...rest } = listingStoreState.appliedFacets;

    resetForNewQuery({ appliedFacets: rest });
  },

  setSort: (sort: ListingStoreState["sort"], option?: ListingSortOption) => {
    resetForNewQuery({ sort, sortOption: option });
  },

  setPage: (page: ListingStoreState["page"]) => {
    listingStoreState = { ...listingStoreState, page };

    emit();
  },

  setLoading: (loading: boolean) => {
    listingStoreState = { ...listingStoreState, loading };

    emit();
  },

  setTree: (tree: ListingStoreState["tree"]) => {
    if (deepEqual(listingStoreState.tree, tree)) {
      return;
    }

    listingStoreState = { ...listingStoreState, tree };

    emit();
  },

  hydrate: (
    state: Partial<
      Pick<
        ListingStoreState,
        | "products"
        | "page"
        | "sort"
        | "sortOption"
        | "sortOptions"
        | "totalPages"
        | "totalResults"
        | "appliedFacets"
        | "facets"
        | "tree"
      >
    >
  ) => {
    listingStoreState = {
      ...listingStoreState,
      ...state,
      facets: state.facets,
      tree: state.tree,
      loading: false,
      revision: listingStoreState.revision + 1,
    };

    emit();
  },

  resetState: () => {
    listingStoreState = { ...INITIAL_STATE };
  },
};
